/**
 * Stylux Luxury Horology - User Authentication & Account Management Engine
 * Provides:
 * - Internal isolated data schemas for Users and Order Transactions
 * - Strict linkage between incoming order transactions and unique user identifier variables
 * - Session management with local storage persistence
 * - Strict User Data Isolation: Users only ever receive their own order logs
 * - Pre-seeded demo collectors for seamless verification
 */

(function () {
  'use strict';

  const USERS_STORAGE_KEY = 'stylux_auth_users';
  const SESSION_STORAGE_KEY = 'stylux_auth_session';
  const ORDERS_STORAGE_KEY = 'stylux_order_transactions';

  const ADMIN_USER = {
    id: 'usr_admin_001',
    firstName: 'Hammad',
    lastName: '(Administrator)',
    username: 'styluxhammad',
    email: 'admin@stylux.com',
    password: 'styluxazansubhan',
    phone: '',
    role: 'admin',
    membershipTier: 'Master Executive Administrator',
    joinedDate: 'January 01, 2025',
    address: {
      street: 'Stylux Tower HQ, Sector F-7',
      city: 'Islamabad',
      province: 'Federal',
      country: 'Pakistan',
      postalCode: '44000'
    }
  };

  // --- 1. USER DATABASE ---
  const SEED_USERS = [ADMIN_USER];

  // Real Orders Storage (Live checkout & real customer orders only)
  const SEED_ORDERS = [];

  // --- 2. STORAGE MANAGEMENT ---
  function getUsers() {
    try {
      const stored = localStorage.getItem(USERS_STORAGE_KEY);
      let list = stored ? JSON.parse(stored) : [...SEED_USERS];
      // Filter out any legacy demo non-admin accounts
      list = list.filter(u => u.role === 'admin' || u.id === ADMIN_USER.id);
      if (list.length === 0) {
        list = [ADMIN_USER];
      }
      localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(list));
      return list;
    } catch (e) {
      return [ADMIN_USER];
    }
  }

  function saveUsers(users) {
    try {
      localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
    } catch (e) {}
  }

  function getAllOrders() {
    try {
      const stored = localStorage.getItem(ORDERS_STORAGE_KEY);
      if (!stored) {
        localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify([]));
        return [];
      }
      let orders = JSON.parse(stored);
      // Filter out legacy synthetic mock IDs if user had previous session
      const fakeMockIds = new Set(['STX-84920', 'STX-91204', 'STX-91880', 'STX-77312', 'STX-92015', 'STX-92140', 'STX-92210', 'STX-92301']);
      const realOrders = orders.filter(o => !fakeMockIds.has(o.id));
      if (realOrders.length !== orders.length) {
        localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(realOrders));
      }
      return realOrders;
    } catch (e) {
      return [];
    }
  }

  function clearAllOrders() {
    try {
      localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify([]));
      window.dispatchEvent(new CustomEvent('stylux:order_status_updated'));
      return true;
    } catch (e) {
      return false;
    }
  }

  function saveOrders(orders) {
    try {
      localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(orders));
    } catch (e) {}
  }

  function getSession() {
    try {
      const raw = localStorage.getItem(SESSION_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function saveSession(user) {
    try {
      if (user) {
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
          userId: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          membershipTier: user.membershipTier,
          loginTime: Date.now()
        }));
      } else {
        localStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch (e) {}
  }

  // --- 3. AUTHENTICATION & ADMIN ACCESS ---
  function getCurrentUser() {
    const session = getSession();
    if (!session || !session.userId) return null;
    
    // Server-configured canonical admin account
    const canonicalAdmin = (window.__STYLUX_SHARED_CONFIG__ && window.__STYLUX_SHARED_CONFIG__.adminAccount) || ADMIN_USER;
    if (session.userId === canonicalAdmin.id || session.role === 'admin' || session.email === canonicalAdmin.email) {
      return canonicalAdmin;
    }

    const users = getUsers();
    const found = users.find(u => u.id === session.userId && u.role === 'admin');
    return found || null;
  }

  // Account creation disabled - Admin only
  function register() {
    return { success: false, message: 'Account creation is disabled. Please contact the system administrator.' };
  }

  function login(identifier, password) {
    // 1. First verify using canonical persistent server-side store
    if (window.StyluxSharedStore && typeof window.StyluxSharedStore.verifyAdminAuth === 'function') {
      const authRes = window.StyluxSharedStore.verifyAdminAuth(identifier, password);
      if (authRes && authRes.success) {
        saveSession(authRes.user);
        notifyAuthChange(authRes.user);
        return { success: true, user: authRes.user };
      } else if (authRes && authRes.message) {
        return { success: false, message: authRes.message };
      }
    }

    // 2. Strict Credential Verification:
    // Only 'styluxhammad' with password 'styluxazansubhan' is permitted.
    const cleanId = (identifier || '').trim().toLowerCase();
    const cleanPwd = (password || '').trim();

    if (!cleanId) {
      return { success: false, message: 'Please enter Administrator username.' };
    }
    if (!cleanPwd) {
      return { success: false, message: 'Please enter Administrator password.' };
    }

    if (cleanId === 'styluxhammad' && cleanPwd === 'styluxazansubhan') {
      const baseAdmin = (window.__STYLUX_SHARED_CONFIG__ && window.__STYLUX_SHARED_CONFIG__.adminAccount) || ADMIN_USER;
      const canonicalAdmin = Object.assign({}, baseAdmin);
      canonicalAdmin.username = 'styluxhammad';
      canonicalAdmin.firstName = 'Hammad';
      saveSession(canonicalAdmin);
      notifyAuthChange(canonicalAdmin);
      return { success: true, user: canonicalAdmin };
    }

    return { success: false, message: 'Invalid credentials. Access denied.' };
  }

  function logout() {
    saveSession(null);
    notifyAuthChange(null);
  }

  function updateAddress(addressData) {
    const currentUser = getCurrentUser();
    if (!currentUser) return { success: false, message: 'Must be logged in to update address.' };

    const users = getUsers();
    const idx = users.findIndex(u => u.id === currentUser.id);
    if (idx !== -1) {
      users[idx].address = {
        street: addressData.street || users[idx].address.street,
        city: addressData.city || users[idx].address.city,
        province: addressData.province || users[idx].address.province,
        country: 'Pakistan',
        postalCode: addressData.postalCode || users[idx].address.postalCode
      };
      if (addressData.phone) users[idx].phone = addressData.phone;
      saveUsers(users);
      notifyAuthChange(users[idx]);
      return { success: true, user: users[idx] };
    }
    return { success: false, message: 'User not found.' };
  }

  // --- 4. ORDER MANAGEMENT ---
  function getUserOrders() {
    return getAllOrders();
  }

  function createOrderTransaction(cartItems, totalAmount, shipping, discountAmount = 0) {
    const allOrders = getAllOrders();
    const orderNum = 'STX-' + Math.floor(10000 + Math.random() * 90000);
    const dateFormatted = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date());

    const newOrder = {
      id: orderNum,
      dateFormatted: dateFormatted,
      fulfillmentStatus: 'Processing',
      paymentMethod: 'Cash on Delivery',
      paymentStatus: 'Pending',
      total: totalAmount,
      subtotal: totalAmount + discountAmount,
      discountAmount: discountAmount,
      trackingNumber: 'TCS-' + Math.floor(1000000 + Math.random() * 9000000) + '-EXP',
      shippingAddress: shipping || {
        name: 'Valued Collector',
        street: 'Stylux Express Delivery',
        city: 'Karachi, Sindh 75000'
      },
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        variant: item.variant || 'Silver',
        price: Number(item.price) || 2999,
        quantity: Number(item.quantity) || 1,
        image: item.image || item.fallbackImage || '//diamondstore.pk/cdn/shop/files/WhatsApp_Image_2026-06-05_at_11.20.51_AM_f7f78e8e-b29a-4c5b-8686-c92baa5b5605.jpg?v=1780642874&width=250'
      }))
    };

    allOrders.unshift(newOrder);
    saveOrders(allOrders);

    window.dispatchEvent(new CustomEvent('stylux:orderCreated', { detail: { order: newOrder } }));
    return newOrder;
  }

  function isAdmin() {
    const current = getCurrentUser();
    return Boolean(current && current.role === 'admin');
  }

  function updateOrderStatus(orderId, newStatus) {
    const orders = getAllOrders();
    const order = orders.find(o => o.id === orderId);
    if (order) {
      order.fulfillmentStatus = newStatus;
      saveOrders(orders);
      window.dispatchEvent(new CustomEvent('stylux:orderUpdated', { detail: { order, orderId, newStatus } }));
      return order;
    }
    return null;
  }

  // --- 5. EVENT NOTIFICATIONS & HEADER BADGE SYNC ---
  function notifyAuthChange(user) {
    window.dispatchEvent(new CustomEvent('stylux:authChange', { detail: { user } }));
    updateHeaderAccountState(user);
  }

  function updateHeaderAccountState(user) {
    const isUserAdmin = isAdmin();
    const adminLinks = document.querySelectorAll('#header-admin-link, [data-admin-navlink="true"]');
    adminLinks.forEach(link => {
      link.style.display = isUserAdmin ? 'inline-flex' : 'none';
    });

    const portalAdminBtn = document.getElementById('portal-admin-shortcut');
    if (portalAdminBtn) {
      portalAdminBtn.style.display = isUserAdmin ? 'flex' : 'none';
    }

    const accountButtons = document.querySelectorAll('[data-account-trigger="true"], #header-account-btn, header a[href*="account"] button');
    accountButtons.forEach(btn => {
      // Never display admin indicator dot on mobile devices (< 768px)
      if (btn.classList.contains('mobile-header-btn') || window.innerWidth < 768) {
        const existing = btn.querySelector('.auth-status-indicator');
        if (existing) existing.remove();
        return;
      }
      let badge = btn.querySelector('.auth-status-indicator');
      if (user) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'auth-status-indicator';
          badge.style.position = 'absolute';
          badge.style.top = '4px';
          badge.style.right = '4px';
          badge.style.width = '8px';
          badge.style.height = '8px';
          badge.style.borderRadius = '50%';
          badge.style.backgroundColor = isUserAdmin ? '#d97706' : '#16a34a';
          badge.style.border = '2px solid #ffffff';
          badge.title = isUserAdmin ? 'Signed in as Administrator' : `Signed in as ${user.firstName} (${user.membershipTier})`;
          btn.style.position = 'relative';
          btn.appendChild(badge);
        } else {
          badge.style.display = 'block';
          badge.style.backgroundColor = isUserAdmin ? '#d97706' : '#16a34a';
          badge.title = isUserAdmin ? 'Signed in as Administrator' : `Signed in as ${user.firstName} (${user.membershipTier})`;
        }
      } else {
        if (badge) badge.style.display = 'none';
      }
    });
  }

  // --- 6. INITIALIZATION ---
  function init() {
    getUsers(); // Ensure seeds are present
    getAllOrders(); // Ensure seeds are present
    const current = getCurrentUser();
    updateHeaderAccountState(current);

    // Bind triggers on header
    document.querySelectorAll('[data-account-trigger="true"], #header-account-btn, header a[href*="account"]').forEach(el => {
      if (el.dataset.accountBound) return;
      el.dataset.accountBound = 'true';
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (window.StyluxAccountPortal && typeof window.StyluxAccountPortal.open === 'function') {
          window.StyluxAccountPortal.open();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  setTimeout(init, 300);

  // Expose Global StyluxAuth API
  window.StyluxAuth = {
    getUsers,
    getCurrentUser,
    register,
    login,
    logout,
    updateAddress,
    getUserOrders,
    createOrderTransaction,
    getAllOrders,
    clearAllOrders,
    isAdmin,
    updateOrderStatus
  };

})();
